<template>
    <!-- Emty tag to make ssr rehydration happy — it complains if the template is empty -->
    <div data-head-meta-game />
</template>
<script setup lang="ts">
import { useHead } from '@unhead/vue'

import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'

import { useConfig } from '@/composables/config'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { SCHEMA_CURRENCY, useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import { CLOUDFLARE_CUSTOMER_URL, MANIFEST_META_KEY } from '@/constants/general'
import { ManifestGenerator } from '@/modules/pwa'
import { type GenerateGameHeadParams, getSubCategories, useGamePlatforms } from '@/pages/Games/utils'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useGamePageStore } from '@/store/game-store'
import { PWA_CLID } from '@/utils/affiliate'
import { getYoutubeId } from '@/utils/helpers'
import { processThumbOriginalSrc } from '@/utils/pg-thumbs-helpers'

const props = withDefaults(
    defineProps<{
        noindex?: boolean
        breadcrumbs?: { href: string | RouteLocationRaw; title: string }[]
    }>(),
    { noindex: false },
)

const tr = useTr()
const router = useRouter()
const gamePageStore = useGamePageStore()
const { game, gameCatInfo } = storeToRefs(gamePageStore)
const { getCanonicalAndAlternates } = useLocalizedRouters()
const { isPartOf, getInLanguage, getFullLink } = useSchemaOrg()
const gameCategories = computed(() => getSubCategories('category', gameCatInfo.value).map((item) => item.title))

const developerInfo = computed(() =>
    game.value?.developer ? tr('headMetaGame.developer', { developer: game.value?.developer }) : '',
)
const metaDescription = computed(() =>
    `${tr('headMetaGame.description', { title: game.value?.title ?? '' })} ${developerInfo.value}`.trim(),
)
const metaTitle = computed(() =>
    `${tr('headMetaGame.title', { title: game.value?.title ?? '' })} ${developerInfo.value}`.trim(),
)

const altImg = computed(() => tr('headMetaGame.alt_image', { title: game.value?.title ?? '' }))

const { VUE_APP_HOST_URL } = useConfig()
const metaURL = computed(() =>
    game.value?.hru
        ? `${VUE_APP_HOST_URL}${router.resolve({ name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value?.hru } }).fullPath}`
        : '',
)

const gamePlatforms = useGamePlatforms(game)

const appStore = useAppStore()
const { platform } = storeToRefs(appStore)

const schemaOrgBreadcrumb = computed(() => ({
    breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: props.breadcrumbs?.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'Thing',
                '@id': getFullLink(item.href),
                name: item.title,
            },
        })),
    },
}))

const createdAt = computed(() => {
    if (!game.value?.createdAt) {
        return undefined
    }
    const date = new Date(game.value.createdAt)
    date.setDate(date.getDate() - 1)
    return date.toISOString().slice(0, 10)
})

const updatedAt = computed(() => {
    if (!game.value?.updatedAt) {
        return undefined
    }
    const date = new Date(game.value.updatedAt)
    return date.toISOString().slice(0, 10)
})

const availableDevices = computed(() => {
    if (!game.value) {
        return []
    }
    const devices = []
    if (game.value.is_desktop) {
        devices.push('Desktop')
    }
    if ((game.value.is_android || game.value.is_ios) && !devices.includes('Mobile')) {
        devices.push('Mobile', 'Tablet')
    }
    return devices
})

const videoDescription = computed(() => {
    return game.value?.video_metadata?.[0] ?? null
})

const youtubeId = computed(() => {
    return getYoutubeId(game.value?.youtube_video)
})

const videoLinks = computed(() => {
    let thumbnail = ''
    let content = ''
    let embed = ''

    if (youtubeId.value) {
        thumbnail = `https://img.youtube.com/vi/${youtubeId.value}/hqdefault.jpg`
        content = `https://www.youtube.com/watch?v=${youtubeId.value}`
        embed = `https://www.youtube.com/embed/${youtubeId.value}`
    } else if (videoDescription.value) {
        thumbnail = videoDescription.value.playgama_id
            ? `${CLOUDFLARE_CUSTOMER_URL}${videoDescription.value.playgama_id}/thumbnails/thumbnail.jpg`
            : processThumbOriginalSrc(game.value?.preview || '')

        content = videoDescription.value.playgama_id
            ? `${CLOUDFLARE_CUSTOMER_URL}${videoDescription.value.playgama_id}/downloads/default.mp4`
            : videoDescription.value.external_url

        embed = videoDescription.value.playgama_id
            ? `${CLOUDFLARE_CUSTOMER_URL}${videoDescription.value.playgama_id}/downloads/default.mp4`
            : videoDescription.value.external_url
    }

    return { thumbnail, content, embed }
})

function getOrganizationSchema() {
    if (!game.value) {
        return {}
    }
    const IMAGE_WIDTH = 1920
    const IMAGE_HEIGHT = 1080

    const currentRoute = getFullLink({ name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value.hru } })
    const pageId = `${currentRoute}#item`
    const appId = `${currentRoute}#app`

    const original = processThumbOriginalSrc(game.value.preview)
    const { title } = game.value || {}

    const videoObject = videoLinks.value?.content
        ? {
              video: {
                  '@type': 'VideoObject',
                  name: `${game.value?.title} - ${tr('seo.gameplay_video')}`,
                  description: metaDescription.value,
                  thumbnailUrl: videoLinks.value?.thumbnail,
                  uploadDate: game.value?.updatedAt,
                  contentUrl: videoLinks.value?.content,
                  embedUrl: videoLinks.value?.embed,
              },
          }
        : {}

    // ItemPage
    const itemPageNode = {
        '@context': 'https://schema.org',
        '@type': 'ItemPage',
        '@id': pageId,
        url: currentRoute,
        name: metaTitle.value,
        description: metaDescription.value,
        inLanguage: getInLanguage(),
        ...(props.breadcrumbs ? schemaOrgBreadcrumb.value : {}),
        primaryImageOfPage: {
            '@type': 'ImageObject',
            url: original,
            contentUrl: original,
            width: IMAGE_WIDTH,
            height: IMAGE_HEIGHT,
        },
        image: {
            '@type': 'ImageObject',
            url: original,
            contentUrl: original,
            width: IMAGE_WIDTH,
            height: IMAGE_HEIGHT,
        },
        ...videoObject,
        mainEntity: { '@id': appId },
        ...isPartOf.value,
    }

    // WebApplication
    const appNode = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        '@id': appId,
        mainEntityOfPage: { '@id': pageId },
        name: title,
        applicationCategory: 'Game',
        additionalType: 'https://schema.org/VideoGame',
        url: currentRoute,
        // for CSE = pagemap.webapplication.image
        image: [original, { '@type': 'ImageObject', url: original, contentUrl: original }],
        applicationSubCategory: gameCategories.value,
        availableOnDevice: availableDevices.value,
        operatingSystem: gamePlatforms.value,
        ...(game.value?.developer ? { author: { '@type': 'Person', name: game.value.developer } } : {}),
        ...(game.value?.rating
            ? {
                  aggregateRating: {
                      '@type': 'AggregateRating',
                      worstRating: 1,
                      bestRating: 5,
                      ratingValue: game.value.rating.value,
                      ratingCount: game.value.rating.count,
                  },
              }
            : {}),
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: SCHEMA_CURRENCY,
            availability: 'https://schema.org/InStock',
        },
        ...(game.value?.screenshots && game.value.screenshots.length > 0
            ? {
                  screenshot: game.value.screenshots.map((s) => ({
                      '@type': 'ImageObject',
                      url: s,
                      contentUrl: s,
                  })),
              }
            : {}),
        ...(createdAt.value ? { datePublished: createdAt.value } : {}),
        ...(updatedAt.value ? { dateModified: updatedAt.value } : {}),
        potentialAction: {
            '@type': 'PlayAction',
            target: currentRoute,
            name: title,
        },
    }

    return [itemPageNode, appNode]
}

// eslint-disable-next-line @typescript-eslint/no-shadow
function generateHead({ game }: GenerateGameHeadParams) {
    if (!game.value) {
        return {}
    }
    const organizationSchema = getOrganizationSchema()
    return {
        title: () => metaTitle.value,
        meta: [
            { name: 'description', content: () => metaDescription.value },
            { property: 'og:title', content: () => metaTitle.value },
            { property: 'og:description', content: () => metaDescription.value },
            { property: 'og:url', content: () => metaURL.value },
            { property: 'og:image', content: () => game.value?.preview },
            { property: 'og:image:alt', content: () => altImg.value },
            {
                property: 'game:platform',
                content: () => gamePlatforms.value,
            },
            { name: 'twitter:title', content: () => metaTitle.value },
            { name: 'twitter:description', content: () => metaDescription.value },
            { name: 'twitter:image', content: () => game.value?.preview },
            { name: 'twitter:image:alt', content: () => altImg.value },
            props.noindex ? { name: 'robots', content: () => 'noindex, follow' } : {},
        ],
        link: [
            ...getCanonicalAndAlternates({
                forRoute: { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value.hru as string } },
            }),
            ...(game.value?.hru && platform.value !== 'ios'
                ? [
                      {
                          rel: 'manifest',
                          href: `/manifest/game/${game.value.hru}.webmanifest?clid=${PWA_CLID}`,
                          key: MANIFEST_META_KEY,
                      },
                  ]
                : []),
            ...(game.value?.icon
                ? [
                      {
                          rel: 'apple-touch-icon',
                          sizes: '180x180',
                          href: ManifestGenerator.getScaledImage(game.value.icon, 180, 180),
                          media: '(prefers-color-scheme:light)',
                          key: 'apple-touch-icon-light',
                      },
                      {
                          rel: 'apple-touch-icon',
                          sizes: '180x180',
                          href: ManifestGenerator.getScaledImage(game.value.icon, 180, 180),
                          media: '(prefers-color-scheme:dark)',
                          key: 'apple-touch-icon-dark',
                      },
                  ]
                : []),
        ],
        script: [
            {
                type: 'application/ld+json',
                innerHTML: organizationSchema,
            },
        ],
    }
}

const pageMeta = computed(() => generateHead({ game }))
useHead(pageMeta)
</script>
